import { pickShopifyMediaByChannel } from './listingMasterConsistency';
import needsShopifyPrepareFormat from './needsShopifyPrepareFormat';
import resolvePublishMedia, {
  PUBLISH_BLOCK_REASONS,
} from './resolvePublishMedia';

export const SYNC_MEDIA_SEVERITY = {
  OK: 'ok',
  SOFT: 'soft',
  HARD: 'hard',
};

/** Soft UX flag: convertible non-native format (Prepare modal on confirm). */
export const SYNC_MEDIA_REASONS = {
  NEEDS_PREPARE: 'needs_prepare',
  THUMBNAIL_PROCESSING: PUBLISH_BLOCK_REASONS.THUMBNAIL_PROCESSING,
  THUMBNAIL_FAILED: PUBLISH_BLOCK_REASONS.THUMBNAIL_FAILED,
  THUMBNAIL_MISSING: PUBLISH_BLOCK_REASONS.THUMBNAIL_MISSING,
  UNSUPPORTED_MEDIA_TYPE: PUBLISH_BLOCK_REASONS.UNSUPPORTED_MEDIA_TYPE,
};

/** Reasons that always disable Confirm while selected (PCS-11014 Bug 9). */
export const HARD_SYNC_MEDIA_REASONS = [
  PUBLISH_BLOCK_REASONS.THUMBNAIL_FAILED,
  PUBLISH_BLOCK_REASONS.THUMBNAIL_MISSING,
  PUBLISH_BLOCK_REASONS.UNSUPPORTED_MEDIA_TYPE,
];

/**
 * Priority among hard reasons for Confirm-disabled tooltip.
 * Includes processing for publish-directly paths where it is hard.
 */
export const HARD_SYNC_MEDIA_REASON_PRIORITY = [
  PUBLISH_BLOCK_REASONS.THUMBNAIL_PROCESSING,
  PUBLISH_BLOCK_REASONS.THUMBNAIL_FAILED,
  PUBLISH_BLOCK_REASONS.THUMBNAIL_MISSING,
  PUBLISH_BLOCK_REASONS.UNSUPPORTED_MEDIA_TYPE,
];

/**
 * Per-asset Shopify validation state for Sync media selection tiles.
 *
 * @param {Object} [asset]
 * @param {{ requirePublishReady?: boolean }} [options]
 *   When true (publish-directly / live), not-ready media including
 *   `thumbnail_processing` is hard and blocks Confirm.
 * @returns {{
 *   severity: ('ok'|'soft'|'hard'),
 *   reason: (string|null),
 *   needsPrepare: boolean,
 *   canPublish: boolean,
 * }}
 */
export default function resolveSyncMediaSelectionState(
  asset = {},
  { requirePublishReady = false } = {},
) {
  const publish = resolvePublishMedia(asset);
  const needsPrepare = needsShopifyPrepareFormat(asset);

  if (!publish.canPublish) {
    const reason = publish.reason || SYNC_MEDIA_REASONS.UNSUPPORTED_MEDIA_TYPE;
    const isProcessing = reason === PUBLISH_BLOCK_REASONS.THUMBNAIL_PROCESSING;

    if (isProcessing && !requirePublishReady) {
      return {
        severity: SYNC_MEDIA_SEVERITY.SOFT,
        reason: SYNC_MEDIA_REASONS.THUMBNAIL_PROCESSING,
        needsPrepare,
        canPublish: false,
      };
    }

    return {
      severity: SYNC_MEDIA_SEVERITY.HARD,
      reason,
      needsPrepare,
      canPublish: false,
    };
  }

  if (needsPrepare) {
    return {
      severity: SYNC_MEDIA_SEVERITY.SOFT,
      reason: SYNC_MEDIA_REASONS.NEEDS_PREPARE,
      needsPrepare: true,
      canPublish: true,
    };
  }

  return {
    severity: SYNC_MEDIA_SEVERITY.OK,
    reason: null,
    needsPrepare: false,
    canPublish: true,
  };
}

/**
 * Aggregates selected Sync media for Confirm disable + tooltips.
 *
 * @param {Object[]} [assets]
 * @param {{ requirePublishReady?: boolean }} [options]
 * @returns {{
 *   hasHardSelected: boolean,
 *   hardCount: number,
 *   primaryReason: (string|null),
 *   processingCount: number,
 *   needsPrepareCount: number,
 * }}
 */
export function aggregateSyncMediaSelectionState(
  assets = [],
  { requirePublishReady = false } = {},
) {
  let hardCount = 0;
  let processingCount = 0;
  let needsPrepareCount = 0;
  const hardReasons = [];

  (assets || []).forEach((asset) => {
    if (!asset) return;
    const state = resolveSyncMediaSelectionState(asset, { requirePublishReady });
    if (state.needsPrepare) {
      needsPrepareCount += 1;
    }
    if (state.reason === SYNC_MEDIA_REASONS.THUMBNAIL_PROCESSING) {
      processingCount += 1;
    }
    if (state.severity === SYNC_MEDIA_SEVERITY.HARD) {
      hardCount += 1;
      hardReasons.push(state.reason);
    }
  });

  if (!hardCount) {
    return {
      hasHardSelected: false,
      hardCount: 0,
      primaryReason: null,
      processingCount,
      needsPrepareCount,
    };
  }

  const primaryReason = HARD_SYNC_MEDIA_REASON_PRIORITY.find((candidate) => (
    hardReasons.includes(candidate)
  )) || hardReasons[0];

  return {
    hasHardSelected: true,
    hardCount,
    primaryReason,
    processingCount,
    needsPrepareCount,
  };
}

/**
 * Channel-aware aggregate: an asset is publish-ready-required when it is
 * selected for any Shopify channel whose action is `publishAction`.
 * Non-Shopify channel selections are ignored (same scope as Prepare).
 *
 * @param {{
 *   assets?: Object[],
 *   mediaByChannel?: Object.<string, string[]>,
 *   channelActionsByChannelId?: Object.<string, string>,
 *   publishAction?: string,
 *   channelOptions?: Array<{ value: string, type?: string }>,
 * }} [params]
 */
export function aggregateSyncMediaSelectionStateByChannel({
  assets = [],
  mediaByChannel = {},
  channelActionsByChannelId = {},
  publishAction = 'publish',
  channelOptions = [],
} = {}) {
  const byId = new Map();
  (assets || []).forEach((asset) => {
    if (asset?._id) {
      byId.set(String(asset._id), asset);
    }
  });

  /** @type {Map<string, boolean>} */
  const requirePublishReadyByAssetId = new Map();

  const shopifyMediaByChannel = pickShopifyMediaByChannel(
    mediaByChannel,
    channelOptions,
  );

  Object.entries(shopifyMediaByChannel).forEach(([channelId, assetIds]) => {
    const isPublishChannel = channelActionsByChannelId[channelId] === publishAction;
    (assetIds || []).forEach((assetId) => {
      const id = String(assetId);
      if (!byId.has(id)) return;
      if (isPublishChannel) {
        requirePublishReadyByAssetId.set(id, true);
      } else if (!requirePublishReadyByAssetId.has(id)) {
        requirePublishReadyByAssetId.set(id, false);
      }
    });
  });

  let hardCount = 0;
  let processingCount = 0;
  let needsPrepareCount = 0;
  const hardReasons = [];

  requirePublishReadyByAssetId.forEach((requirePublishReady, assetId) => {
    const asset = byId.get(assetId);
    const state = resolveSyncMediaSelectionState(asset, { requirePublishReady });
    if (state.needsPrepare) {
      needsPrepareCount += 1;
    }
    if (state.reason === SYNC_MEDIA_REASONS.THUMBNAIL_PROCESSING) {
      processingCount += 1;
    }
    if (state.severity === SYNC_MEDIA_SEVERITY.HARD) {
      hardCount += 1;
      hardReasons.push(state.reason);
    }
  });

  if (!hardCount) {
    return {
      hasHardSelected: false,
      hardCount: 0,
      primaryReason: null,
      processingCount,
      needsPrepareCount,
    };
  }

  const primaryReason = HARD_SYNC_MEDIA_REASON_PRIORITY.find((candidate) => (
    hardReasons.includes(candidate)
  )) || hardReasons[0];

  return {
    hasHardSelected: true,
    hardCount,
    primaryReason,
    processingCount,
    needsPrepareCount,
  };
}
